import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@api/client';
import { useAuthStore } from '@store/authStore';
import { useNotificationStore } from '@store/notificationStore';
import type { TenantSummary, User } from '@store/authStore';

interface LoginRequest {
  email: string;
  password: string;
}

interface LoginResponse {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  is_admin: boolean;
  onboarding_complete: boolean;
  active_tenant_id?: string | null;
  active_tenant?: TenantSummary | null;
  available_tenants?: TenantSummary[];
  permissions?: string[];
}

export const useLogin = () => {
  const { setAuth } = useAuthStore();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (credentials: LoginRequest) => {
      const { data } = await apiClient.post<LoginResponse>(
        '/auth/login',
        credentials
      );
      return data;
    },
    onSuccess: (data) => {
      // Convert response to User model
      const user: User = {
        id: data.id,
        email: data.email,
        first_name: data.first_name,
        last_name: data.last_name,
        status: 'active',
        is_admin: data.is_admin,
        onboarding_complete: data.onboarding_complete,
        active_tenant_id: data.active_tenant_id ?? null,
        active_tenant: data.active_tenant ?? null,
        available_tenants: data.available_tenants ?? [],
        permissions: data.permissions ?? [],
      };

      // Store user metadata; session cookie is HttpOnly and sent automatically.
      setAuth(user);

      addNotification({
        message: `Welcome back, ${data.first_name}!`,
        type: 'success',
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        'Login failed. Please try again.';

      addNotification({
        message: errorMessage,
        type: 'error',
      });
    },
  });
};
